/**
 * main — client bootstrap & scene assembly.
 * The engine owns light, air and time; scenes own content and flow.
 * This file only wires them together: context, HUD, settings → live
 * theme/quality/audio, and the Scene lifecycle (enter/exit/update).
 */
import './ui/ui.css';
import { GameEngine } from './core/GameEngine';
import { AudioManager } from './core/AudioManager';
import { TitleScene } from './scenes/TitleScene';
import { GameScene } from './scenes/GameScene';
import { ResultScene } from './scenes/ResultScene';
import type { AppContext, Scene } from './scenes/context';
import { createHud, type HudHandle } from './ui/hud';
import { createDebugPanel, type DebugPanelHandle } from './ui/debugPanel';
import { openSettings } from './ui/settingsPanel';
import { $settings, $theme, $quality, updateSettings } from './store/settingsStore';
import { $net } from './store/netStore';
import { netClient } from './net/netClient';
import { APP_VERSION, logger } from './debug/logger';
import type { SceneId } from '@shared/types/game';

type SceneFactory = () => Scene;

class App {
  private engine = new GameEngine();
  private audio = new AudioManager();
  private ctx!: AppContext;
  private hud!: HudHandle;
  private overlay!: HTMLElement;
  private current: Scene | null = null;
  private unsubs: (() => void)[] = [];
  private debugPanel!: DebugPanelHandle;
  /** 已自动导航进入的对局 id：同场重连 STATE_SYNC 不重建场景，rematch 新房必导航。 */
  private netNavMatchId: string | null = null;

  /**
   * Fresh instance per transition — GameScene freezes match settings at
   * construction, so scenes must never be reused across games.
   * ('lobby' has no dedicated stage; the title parlour is the hub.)
   */
  private factories: Partial<Record<SceneId, SceneFactory>> = {
    title: () => new TitleScene(),
    game: () => new GameScene(),
    result: () => new ResultScene(),
  };

  constructor() {
    this.buildUi();
    this.buildContext();

    this.engine.init(document.getElementById('app') ?? document.body, $quality.get(), $theme.get());

    this.bindStores();
    this.engine.setFrameCallback((dt, elapsed) => this.current?.update?.(dt, elapsed));

    this.goTo('title');
    this.hideLoadingScreen();
  }

  // ── UI scaffolding ──────────────────────────────────────
  private buildUi(): void {
    // #ui is the fixed full-screen DOM layer above the canvas.
    const ui = document.createElement('div');
    ui.id = 'ui';
    document.body.appendChild(ui);

    // Dedicated layer for scene overlays (title / result / modal).
    const overlay = document.createElement('div');
    overlay.id = 'scene-overlay';
    ui.appendChild(overlay);
    this.overlay = overlay;

    this.hud = createHud(ui, {
      onSettings: () => {
        void this.audio.init();
        this.openSettings();
      },
      onSound: () => updateSettings({ muted: !$settings.get().muted }),
      onHome: () => {
        // 联机对局中离场 = 主动认输退房（对齐 ResultScene home 语义）；
        // leaveGame 内部清会话并回落 idle，终局态则为非认输退房。
        if ($settings.get().mode === 'online' && $net.get().status === 'in-match') {
          netClient.leaveGame();
        }
        this.goTo('title');
      },
      onDebug: () => this.debugPanel.toggle(),
      getMuted: () => $settings.get().muted,
    });

    // 调试日志窗口（恒驻，随 HUD 调试按钮开合）；场景经 provider 注入棋盘布局。
    this.debugPanel = createDebugPanel(ui, logger, {
      summaryProvider: () => this.debugSummary(),
      layoutProvider: () => this.debugLayout(),
    });
  }

  /** 当前场景若为对局场景则给出调试摘要，否则提示无场景。 */
  private debugSummary(): string {
    const s = this.current as unknown as { debugSummary?: () => string } | null;
    return s?.debugSummary?.() ?? `v${APP_VERSION} · ${$net.get().status}`;
  }

  private debugLayout(): string {
    const s = this.current as unknown as { debugLayout?: () => string } | null;
    return s?.debugLayout?.() ?? '';
  }

  private buildContext(): void {
    this.ctx = {
      engine: this.engine,
      audio: this.audio,
      hud: this.hud,
      overlay: this.overlay,
      goTo: (id) => this.goTo(id),
      openSettings: () => this.openSettings(),
    };
  }

  // ── Live stores → engine / audio ────────────────────────
  private bindStores(): void {
    // nanostores fires subscribers immediately; engine is ready by now.
    this.unsubs.push(
      $theme.subscribe((theme) => {
        this.engine.applyTheme(theme);
        this.current?.onThemeChange?.(theme);
      }),
      $quality.subscribe((tier) => this.engine.applyQuality(tier)),
      $settings.subscribe((s) => {
        this.audio.setSfxVolume(s.sfxVolume);
        this.audio.setAmbientVolume(s.ambientVolume);
        this.audio.setMuted(s.muted);
      }),
      // 联机会话 → 场景导航的唯一权威：匹配开局 GAME_START / 断线重连
      // STATE_SYNC / rematch 新房三合一。快照（match+gameStart）齐且不在
      // 终局窗口期时进入对局；同场重连不重建场景（GameScene 自己重放）。
      $net.subscribe((s) => {
        if (s.status === 'in-match' && s.match !== null && s.gameStart !== null && !s.gameOver) {
          const id = s.gameStart.matchId;
          if (id === this.netNavMatchId && this.current instanceof GameScene) return;
          this.netNavMatchId = id;
          this.goTo('game');
        } else {
          this.netNavMatchId = null;
        }
      })
    );
  }

  // ── Flow ────────────────────────────────────────────────
  goTo(id: SceneId): void {
    const make = this.factories[id] ?? this.factories.title;
    if (!make) return;
    this.current?.exit();
    this.current = null;
    const scene = make();
    this.current = scene;
    scene.enter(this.ctx);
  }

  openSettings(): void {
    openSettings(
      this.overlay,
      (text) => this.hud.toast(text),
      () => {}
    );
  }

  private hideLoadingScreen(): void {
    const el = document.getElementById('loading');
    if (el) {
      setTimeout(() => el.classList.add('hidden'), 100);
      setTimeout(() => el.remove(), 600);
    }
  }
}

// Boot
const app = new App();

// Expose globally for debugging (window.__APP__ + 调试日志器句柄)
(window as unknown as { __APP__: App }).__APP__ = app;
(window as unknown as { __DEBUG__: unknown }).__DEBUG__ = { logger, app };

logger.info('应用启动', {
  event: 'app.started',
  version: APP_VERSION,
  lang: $settings.get().language,
  theme: $theme.get(),
  quality: $quality.get(),
});

export default app;
